import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'baseui/snackbar'
import { useTranslation } from 'react-i18next'

import { selectPodUpdaterStoreData } from './podUpdaterSelectors'
import {
  setMode,
  setProviderUuid,
  setPodsText,
  reset,
  setBusinessType,
  setCountry,
} from './podUpdaterSlice'
import { podUpdaterActions } from './podUpdaterSagas'
import { parsePodsList, canSubmitPodUpdater } from './utils/podUpdaterFormatters'

import {
  BUSINESS_VERTICAL_ENERGY,
  BUSINESS_VERTICAL_INSURANCE,
  BUSINESS_VERTICAL_TELECOM,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/Common/constants'

const BUSINESS_TYPE_OPTIONS = [
  { id: BUSINESS_VERTICAL_ENERGY, label: 'Energy' },
  { id: BUSINESS_VERTICAL_TELECOM, label: 'Telecom' },
  { id: BUSINESS_VERTICAL_INSURANCE, label: 'Insurance' },
]

const COUNTRY_OPTIONS = [
  { id: 'fr', label: 'France' },
  { id: 'be', label: 'Belgium' },
  { id: 'it', label: 'Italy' },
]

const usePodUpdater = () => {
  const dispatch = useDispatch()
  const { enqueue } = useSnackbar()
  const { t } = useTranslation('devToolsNew')

  const {
    mode,
    providerUuid,
    providers,
    podsText,
    isSubmitting,
    resultError,
    resultMessage,
    providersError,
    isProvidersLoading,
    businessType,
    country,
  } = useSelector(selectPodUpdaterStoreData)

  useEffect(() => {
    dispatch(reset())
  }, [])

  useEffect(() => {
    if (!businessType || !country) return

    dispatch({
      type: podUpdaterActions.FETCH_PROVIDERS,
      enqueue,
      t,
      payload: { businessType, country },
    })
  }, [businessType, country])

  const submitLabel =
    mode === 'net'
      ? t('pod_updater.actions.sign_to_net')
      : t('pod_updater.actions.sign_to_abandon')

  const podsCount = useMemo(() => parsePodsList(podsText).length, [podsText])

  const selectedProviderOption = useMemo(() => {
    if (!providerUuid) return null
    return providers.find(provider => String(provider.id) === String(providerUuid)) || null
  }, [providerUuid, providers])

  const isFormValid = useMemo(
    () => canSubmitPodUpdater({ providerUuid, podsText }),
    [providerUuid, podsText],
  )

  const selectedBusinessTypeOption = businessType
    ? BUSINESS_TYPE_OPTIONS.find(option => option.id === businessType) || null
    : null

  const selectedCountryOption = country
    ? COUNTRY_OPTIONS.find(option => option.id === country) || null
    : null

  const onModeChange = next => dispatch(setMode(next))
  const onProviderChange = next => dispatch(setProviderUuid(next?.id ?? null))
  const onPodsTextChange = value => dispatch(setPodsText(value))
  const onBusinessTypeChange = next => dispatch(setBusinessType(next?.id ?? null))
  const onCountryChange = next => dispatch(setCountry(next?.id ?? null))

  const onSubmit = () => {
    dispatch({ type: podUpdaterActions.SUBMIT, enqueue, t })
  }

  return {
    form: {
      mode,
      submitLabel,

      provider: selectedProviderOption,
      providerUuid,
      providers,
      providersError,
      isProvidersLoading,

      podsText,
      podsCount,
      isSubmitting,
      isValid: isFormValid,

      businessTypeOptions: BUSINESS_TYPE_OPTIONS,
      countryOptions: COUNTRY_OPTIONS,
      businessTypeOption: selectedBusinessTypeOption,
      countryOption: selectedCountryOption,

      onBusinessTypeChange,
      onCountryChange,
      onModeChange,
      onProviderChange,
      onPodsTextChange,
      onSubmit,
    },
    result: {
      error: resultError,
      message: resultMessage,
    },
    t,
  }
}

export default usePodUpdater
